import Footer from "../landing-page/components/Footer";

export default function TermsPage() {
  return (
    <div className="bg-background text-foreground min-h-screen">
      <main className="isolate">
        <div className="mx-auto max-w-4xl px-6 py-16 lg:px-8 lg:py-24">
          <div className="mx-auto max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
              Conditions Générales d'Utilisation
            </h1>
            <p className="mt-4 text-sm text-muted-foreground">
              Dernière mise à jour : 10 mars 2026
            </p>

            <div className="mt-10 space-y-10 text-base leading-7 text-muted-foreground">

              {/* Préambule */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Préambule</h2>
                <p>
                  Les présentes Conditions Générales d'Utilisation (ci-après « CGU ») régissent l'accès et l'utilisation
                  de la plateforme Edgely, accessible à l'adresse <span className="text-foreground font-medium">edgely.io</span>,
                  éditée par Edgely (ci-après « Edgely », « nous » ou « l'Éditeur »).
                </p>
                <p className="mt-3">
                  En créant un compte ou en utilisant le service, vous acceptez sans réserve les présentes CGU. Si vous
                  n'acceptez pas ces conditions, vous ne devez pas utiliser le service.
                </p>
              </section>

              {/* 1. Identification de l'éditeur */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">1. Identification de l'éditeur</h2>
                <div className="p-4 bg-muted rounded-lg space-y-1">
                  <p><strong className="text-foreground">Edgely</strong> (auto-entrepreneur)</p>
                  <p>173 rue de Courcelles, 75017 Paris, France</p>
                  <p>Contact : <a href="mailto:contact@edgely.trade" className="text-primary hover:underline">contact@edgely.trade</a></p>
                </div>
                <p className="mt-4 text-sm">
                  Hébergeur : Amazon Web Services EMEA SARL, 38 Avenue John F. Kennedy, L-1855, Luxembourg.
                </p>
              </section>

              {/* 2. Description du service */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">2. Description du service</h2>
                <p>
                  Edgely est une plateforme SaaS (Software as a Service) de journal de trading destinée aux traders
                  particuliers. Elle permet notamment de :
                </p>
                <ul className="mt-4 list-disc pl-6 space-y-1">
                  <li>Enregistrer, importer et analyser des trades</li>
                  <li>Suivre des statistiques de performance personnalisées</li>
                  <li>Gérer des routines, objectifs et sessions de trading</li>
                  <li>Rédiger des analyses et notes de trading (texte et images)</li>
                </ul>
                <p className="mt-4">
                  <strong className="text-foreground">Edgely n'est pas un service financier réglementé.</strong> La
                  plateforme est un outil d'organisation et de suivi personnel. Elle ne fournit aucun conseil en
                  investissement, aucune recommandation de trading, aucune gestion de portefeuille, et n'est pas habilitée
                  à fournir des services d'investissement au sens de la Directive MIF II. Les informations affichées ne
                  constituent pas des conseils financiers.
                </p>
              </section>

              {/* 3. Accès au service et conditions d'inscription */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">3. Accès au service et inscription</h2>

                <h3 className="text-base font-semibold text-foreground mt-4 mb-2">3.1 Conditions d'accès</h3>
                <p>
                  L'accès au service est strictement réservé aux personnes physiques majeures (18 ans ou plus) disposant
                  de la pleine capacité juridique. En créant un compte, vous déclarez sur l'honneur avoir atteint l'âge de
                  18 ans et être juridiquement capable de conclure un contrat.
                </p>
                <p className="mt-3">
                  Le service est destiné à un usage personnel et non commercial, sauf autorisation expresse écrite d'Edgely.
                </p>

                <h3 className="text-base font-semibold text-foreground mt-6 mb-2">3.2 Création de compte</h3>
                <p>
                  L'inscription nécessite la fourniture d'une adresse e-mail valide et la création d'un mot de passe sécurisé.
                  Vous êtes responsable de la confidentialité de vos identifiants et de toute activité effectuée depuis
                  votre compte. En cas de compromission de votre compte, vous devez nous en informer immédiatement à{" "}
                  <a href="mailto:contact@edgely.trade" className="text-primary hover:underline">contact@edgely.trade</a>.
                </p>
                <p className="mt-3">
                  Vous vous engagez à fournir des informations exactes et à les maintenir à jour. Tout compte créé avec
                  de fausses informations pourra être suspendu ou supprimé.
                </p>
              </section>

              {/* 4. Plans et tarification */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">4. Plans et tarification</h2>

                <h3 className="text-base font-semibold text-foreground mt-4 mb-2">4.1 Plans disponibles</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Plan</th>
                        <th className="text-left py-2 pr-4 font-semibold text-foreground">Prix</th>
                        <th className="text-left py-2 font-semibold text-foreground">Caractéristiques principales</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr>
                        <td className="py-3 pr-4 font-medium text-foreground">Free</td>
                        <td className="py-3 pr-4">Gratuit</td>
                        <td className="py-3">1 compte de trading, trades illimités, sessions illimitées, analytics de base, 3 routines, 3 objectifs</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-medium text-foreground">Pro</td>
                        <td className="py-3 pr-4">€15 TTC/mois</td>
                        <td className="py-3">Trades illimités, analytics avancés, routines/objectifs/sessions illimités</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-medium text-foreground">Pro Annuel</td>
                        <td className="py-3 pr-4">€144 TTC/an (renouvelable)</td>
                        <td className="py-3">Toutes les fonctionnalités Pro, facturation annuelle</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-medium text-foreground">Max</td>
                        <td className="py-3 pr-4">€29 TTC/mois</td>
                        <td className="py-3">Toutes les fonctionnalités Pro plus la synchronisation broker automatique (MetaAPI, Tradovate)</td>
                      </tr>
                      <tr>
                        <td className="py-3 pr-4 font-medium text-foreground">Max Annuel</td>
                        <td className="py-3 pr-4">€279 TTC/an (renouvelable)</td>
                        <td className="py-3">Toutes les fonctionnalités Max, facturation annuelle</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="mt-4 text-sm">
                  Les prix s'entendent TTC. Edgely se réserve le droit de modifier sa grille tarifaire avec un préavis
                  de 30 jours. Les abonnements en cours ne sont pas affectés pendant leur période de validité.
                </p>

                <h3 className="text-base font-semibold text-foreground mt-6 mb-2">4.2 Paiement</h3>
                <p>
                  Les paiements sont traités par Stripe, Inc. Les abonnements Pro sont facturés mensuellement par
                  reconduction tacite. Vous pouvez résilier à tout moment depuis votre espace personnel, avec effet à la
                  fin de la période de facturation en cours.
                </p>

                <h3 className="text-base font-semibold text-foreground mt-6 mb-2">4.3 Limites du plan Free</h3>
                <p>
                  Le plan Free est limité à 500 trades. Au-delà de cette limite, le trade le plus ancien est automatiquement
                  archivé (politique FIFO) pour faire place au nouveau trade. L'utilisateur en est informé lors de l'ajout.
                </p>
              </section>

              {/* 5. Droit de rétractation */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">5. Droit de rétractation</h2>
                <p>
                  Conformément aux articles L.221-18 et suivants du Code de la consommation, vous disposez d'un droit de
                  rétractation de <strong className="text-foreground">14 jours calendaires</strong> à compter de la date
                  de souscription à un plan payant, sans avoir à justifier de motif ni à payer de pénalité.
                </p>
                <p className="mt-3">
                  Pour exercer ce droit, contactez-nous avant l'expiration du délai à :{" "}
                  <a href="mailto:contact@edgely.trade" className="text-primary hover:underline">contact@edgely.trade</a>,
                  en indiquant votre nom, email et la date de souscription.
                </p>
                <p className="mt-3">
                  <strong className="text-foreground">Exception :</strong> Si vous avez expressément demandé l'exécution
                  immédiate du service et renoncé à votre droit de rétractation lors de la souscription (conformément à
                  l'article L.221-28 du Code de la consommation), aucun remboursement ne pourra être effectué au titre
                  de la période déjà consommée.
                </p>
                <p className="mt-3">
                  En cas de rétractation valide, le remboursement sera effectué dans les 14 jours suivant la réception
                  de votre demande, par le même moyen de paiement que celui utilisé lors de l'achat.
                </p>
              </section>

              {/* 6. Obligations de l'utilisateur */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">6. Obligations et responsabilités de l'utilisateur</h2>
                <p>En utilisant le service, vous vous engagez à :</p>
                <ul className="mt-4 list-disc pl-6 space-y-2">
                  <li>Ne pas utiliser le service à des fins illégales ou contraires à l'ordre public</li>
                  <li>Ne pas tenter de compromettre la sécurité, l'intégrité ou la disponibilité du service</li>
                  <li>Ne pas accéder aux données d'autres utilisateurs</li>
                  <li>Ne pas effectuer de rétro-ingénierie, décompilation ou désassemblage du service</li>
                  <li>Ne pas utiliser des robots, scrapers ou outils automatisés non autorisés</li>
                  <li>Ne pas revendre, sous-licencier ou exploiter commercialement le service sans autorisation</li>
                  <li>Respecter les droits de propriété intellectuelle d'Edgely et des tiers</li>
                </ul>
                <p className="mt-4">
                  Vous êtes seul responsable des données que vous saisissez dans le service (trades, notes, analyses).
                  Edgely ne saurait être tenu responsable de l'exactitude des données que vous importez ni des décisions
                  de trading que vous pourriez prendre en vous basant sur des informations affichées par la plateforme.
                </p>
              </section>

              {/* 7. Propriété intellectuelle */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">7. Propriété intellectuelle</h2>

                <h3 className="text-base font-semibold text-foreground mt-4 mb-2">7.1 Droits d'Edgely</h3>
                <p>
                  Edgely et ses contenus (code source, design, interface, marque, logo, textes, algorithmes d'analyse)
                  sont protégés par le droit de la propriété intellectuelle. Toute reproduction, représentation,
                  modification ou exploitation non autorisée est interdite.
                </p>

                <h3 className="text-base font-semibold text-foreground mt-6 mb-2">7.2 Vos données</h3>
                <p>
                  Vous conservez l'intégralité des droits sur les données que vous saisissez dans le service (trades,
                  notes, analyses, fichiers). Vous accordez à Edgely une licence limitée, non exclusive et non transférable
                  sur ces données, uniquement dans le but de fournir le service (stockage, affichage, calcul de statistiques).
                  Edgely n'utilise jamais vos données à des fins commerciales, de formation de modèles d'IA, ou de revente.
                </p>

                <h3 className="text-base font-semibold text-foreground mt-6 mb-2">7.3 Exportation de vos données</h3>
                <p>
                  Vous pouvez exporter vos données à tout moment depuis votre espace personnel. En cas de fermeture de
                  compte, vos données vous sont rendues accessibles pendant 30 jours avant suppression définitive.
                </p>
              </section>

              {/* 8. Disponibilité et maintenance */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">8. Disponibilité du service</h2>
                <p>
                  Edgely s'efforce de maintenir le service disponible 24h/24 et 7j/7. Toutefois, des interruptions
                  peuvent survenir pour des raisons de maintenance, d'incidents techniques ou de force majeure. Nous
                  nous efforçons d'informer les utilisateurs des maintenances planifiées avec un préavis raisonnable.
                </p>
                <p className="mt-3">
                  Edgely ne garantit pas une disponibilité sans interruption et ne saurait être tenu responsable des
                  pertes ou préjudices résultant d'une indisponibilité temporaire du service.
                </p>
              </section>

              {/* 9. Limitation de responsabilité */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">9. Limitation de responsabilité</h2>
                <p>
                  Dans les limites autorisées par la loi applicable, Edgely ne pourra être tenu responsable de :
                </p>
                <ul className="mt-4 list-disc pl-6 space-y-2">
                  <li>Tout dommage indirect, accessoire ou consécutif lié à l'utilisation du service</li>
                  <li>Toute perte financière résultant de décisions de trading prises sur la base des données affichées</li>
                  <li>La perte de données due à un comportement de l'utilisateur ou à un cas de force majeure</li>
                  <li>Les interruptions de service indépendantes de notre volonté</li>
                </ul>
                <p className="mt-4">
                  La responsabilité totale d'Edgely envers vous, pour quelque cause que ce soit, ne pourra excéder les
                  sommes effectivement versées par vous à Edgely au cours des 12 derniers mois précédant l'événement à
                  l'origine du dommage.
                </p>
                <p className="mt-3 text-sm">
                  Ces limitations ne s'appliquent pas en cas de dol, de faute lourde ou de manquement à une obligation
                  essentielle du contrat.
                </p>
              </section>

              {/* 10. Résiliation */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">10. Résiliation</h2>

                <h3 className="text-base font-semibold text-foreground mt-4 mb-2">10.1 Résiliation par l'utilisateur</h3>
                <p>
                  Vous pouvez résilier votre abonnement à tout moment depuis votre espace personnel. La résiliation
                  prend effet à la fin de la période de facturation en cours. Pour le plan Free, vous pouvez supprimer
                  votre compte à tout moment depuis les paramètres.
                </p>

                <h3 className="text-base font-semibold text-foreground mt-6 mb-2">10.2 Résiliation par Edgely</h3>
                <p>
                  Edgely se réserve le droit de suspendre ou de résilier votre compte, avec ou sans préavis selon la
                  gravité, en cas de :
                </p>
                <ul className="mt-2 list-disc pl-6 space-y-1">
                  <li>Violation des présentes CGU</li>
                  <li>Comportement frauduleux ou abusif</li>
                  <li>Non-paiement</li>
                  <li>Demande d'une autorité judiciaire ou administrative compétente</li>
                </ul>
                <p className="mt-3">
                  En cas de résiliation pour manquement grave, aucun remboursement ne sera effectué pour la période restante.
                </p>
              </section>

              {/* 11. Données personnelles */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">11. Données personnelles</h2>
                <p>
                  Le traitement de vos données personnelles est régi par notre{" "}
                  <a href="/privacy" className="text-primary hover:underline">Politique de confidentialité</a>,
                  qui fait partie intégrante des présentes CGU. Nous vous invitons à la lire attentivement.
                </p>
              </section>

              {/* 12. Médiation */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">12. Médiation de la consommation</h2>
                <p>
                  Conformément aux articles L.616-1 et R.616-1 du Code de la consommation, en cas de litige non résolu
                  à l'amiable, vous pouvez recourir gratuitement à un médiateur de la consommation.
                </p>
                <p className="mt-3">
                  Nous vous recommandons de nous contacter en premier lieu à{" "}
                  <a href="mailto:contact@edgely.trade" className="text-primary hover:underline">contact@edgely.trade</a>{" "}
                  afin de trouver une solution amiable. En l'absence de réponse satisfaisante dans un délai de 2 mois,
                  vous pouvez saisir le médiateur CM2C (Centre de la Médiation de la Consommation de Conciliateurs de Justice) :
                </p>
                <div className="mt-3 p-4 bg-muted rounded-lg">
                  <p><strong className="text-foreground">CM2C</strong></p>
                  <p>14 rue Saint Jean — 75017 Paris</p>
                  <p>
                    <a href="https://www.cm2c.net" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                      www.cm2c.net
                    </a>
                  </p>
                </div>
                <p className="mt-3 text-sm">
                  Vous pouvez également accéder à la plateforme européenne de résolution des litiges en ligne :{" "}
                  <a href="https://ec.europa.eu/consumers/odr" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                    ec.europa.eu/consumers/odr
                  </a>
                </p>
              </section>

              {/* 13. Modifications des CGU */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">13. Modifications des CGU</h2>
                <p>
                  Edgely se réserve le droit de modifier les présentes CGU à tout moment. Les modifications substantielles
                  seront notifiées par email au moins <strong className="text-foreground">30 jours avant leur entrée en vigueur</strong>.
                  La version en vigueur est toujours consultable à l'adresse{" "}
                  <span className="text-foreground font-medium">edgely.io/terms</span>.
                </p>
                <p className="mt-3">
                  En cas de refus des nouvelles conditions, vous disposez du droit de résilier votre compte avant leur
                  entrée en vigueur. La poursuite de l'utilisation du service vaut acceptation des nouvelles CGU.
                </p>
              </section>

              {/* 14. Droit applicable et juridiction */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">14. Droit applicable et juridiction</h2>
                <p>
                  Les présentes CGU sont régies par le <strong className="text-foreground">droit français</strong>.
                </p>
                <p className="mt-3">
                  En cas de litige relatif à l'interprétation ou à l'exécution des présentes, et après échec de la
                  procédure de médiation décrite à l'article 12, les tribunaux compétents seront ceux du ressort de
                  Paris, sauf disposition légale impérative contraire (notamment en matière de protection des
                  consommateurs, où la juridiction du domicile du consommateur est compétente).
                </p>
              </section>

              {/* 15. Dispositions diverses */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">15. Dispositions diverses</h2>
                <p>
                  Si une clause des présentes CGU était déclarée nulle ou inapplicable par une juridiction compétente,
                  les autres clauses demeureraient pleinement en vigueur.
                </p>
                <p className="mt-3">
                  Le fait pour Edgely de ne pas se prévaloir d'une disposition des présentes CGU ne saurait être
                  interprété comme une renonciation à s'en prévaloir ultérieurement.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-xl font-semibold text-foreground mb-4">Contact</h2>
                <div className="p-4 bg-muted rounded-lg space-y-1">
                  <p><strong className="text-foreground">Edgely</strong></p>
                  <p>173 rue de Courcelles, 75017 Paris, France</p>
                  <p>
                    Support :{" "}
                    <a href="mailto:contact@edgely.trade" className="text-primary hover:underline">
                      contact@edgely.trade
                    </a>
                  </p>
                  <p>
                    Données personnelles :{" "}
                    <a href="mailto:contact@edgely.trade" className="text-primary hover:underline">
                      contact@edgely.trade
                    </a>
                  </p>
                </div>
              </section>

            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
